import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { GET_HOMEPAGE_COMPETITIONS } from "MVVM/GraphQL/competitions";
import { ICompetition } from "../../Competitions/@types";
import { getCompetitionPhase } from "../../Competitions/utils";

const POOL_SIZE = 10;

export const CompetitionsBannerViewModel = () => {
  const { data, loading } = useQuery(GET_HOMEPAGE_COMPETITIONS, {
    variables: { first: POOL_SIZE },
    fetchPolicy: "cache-and-network",
  });

  const { featured, others } = useMemo(() => {
    const nodes = (field: "active" | "upcoming" | "ended") =>
      (data?.[field]?.nodes ?? []) as ICompetition[];

    // Phase is re-checked client-side in case a competition flipped status
    // between the server responding and now.
    const active = nodes("active").filter(
      (c) => getCompetitionPhase(c) === "active",
    );
    const upcoming = nodes("upcoming")
      .filter((c) => getCompetitionPhase(c) === "upcoming")
      .sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
      );

    const featured =
      active.find((c) => c.sponsored) ??
      upcoming.find((c) => c.sponsored) ??
      null;

    const live = [...active, ...upcoming].filter(
      (c) => c._id !== featured?._id,
    );

    // Most-recently-ended first, so the row stays fresh and drives FOMO.
    const recentlyEnded = nodes("ended")
      .filter((c) => getCompetitionPhase(c) === "ended")
      .sort(
        (a, b) => new Date(b.endsAt).getTime() - new Date(a.endsAt).getTime(),
      );

    // Active competitions first, then upcoming, then ended backfill. The
    // View trims this to however many cards fill exactly one row, so hand
    // it the full ordered candidate list (no dead space, no wrap).
    const others = [...live, ...recentlyEnded];

    return { featured, others };
  }, [data]);

  return { featured, others, loading };
};
